import type { Contribution, ReactionSummary } from '../api/types';
import { formatTime, normalizeWaveform } from '../lib/mediaUtils';
import styles from './ContributionCard.module.css';
import sharedStyles from './ContributionShared.module.css';

// A focused rail for the PWA (the backend enum has ten types; these five are the
// friendly, jargon-free ones that read at a glance). Order = display order.
const REACTION_RAIL: Array<{ type: string; emoji: string; label: string }> = [
  { type: 'heart', emoji: '❤️', label: 'Heart' },
  { type: 'laugh', emoji: '😂', label: 'Laugh' },
  { type: 'wow', emoji: '😮', label: 'Wow' },
  { type: 'fire', emoji: '🔥', label: 'Fire' },
  { type: 'clap', emoji: '👏', label: 'Clap' },
];

// The summary entry for one contribution.
type ReactionEntry = ReactionSummary[string];

interface ContributionCardProps {
  contribution: Contribution;
  authorName?: string;
  onBuildOn?: (contribution: Contribution) => void;
  onJumpToParent?: (parentContributionId: string) => void;
  onToggleReplies?: (contribution: Contribution) => void;
  repliesExpanded?: boolean;
  replyCount?: number;
  cardRef?: (element: HTMLDivElement | null) => void;
  compact?: boolean;
  authorColor?: string;
  // Reaction wiring — the rail only renders when a handler is supplied (the
  // read-only capture feeds omit it). `reactions` is this card's summary entry.
  threadId?: string;
  reactions?: ReactionEntry;
  onReact?: (contributionId: string, reactionType: string) => void;
}

const ALLOWED_MEDIA_HOSTS = (import.meta.env.VITE_ALLOWED_MEDIA_HOSTS || '').split(',').filter(Boolean);

function isValidMediaUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    if (ALLOWED_MEDIA_HOSTS.length > 0) {
      return ALLOWED_MEDIA_HOSTS.some(
        (host: string) => parsed.hostname === host || parsed.hostname.endsWith('.' + host),
      );
    }
    return true;
  } catch {
    return false;
  }
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(dateStr).toLocaleDateString();
}

function getInitials(name: string): string {
  return name.split(' ').map((word) => word[0]).join('').slice(0, 2).toUpperCase();
}

const AUTHOR_COLORS = [
  'var(--st-author-1)', 'var(--st-author-2)', 'var(--st-author-3)',
  'var(--st-author-4)', 'var(--st-author-5)', 'var(--st-author-6)',
  'var(--st-author-7)', 'var(--st-author-8)', 'var(--st-author-9)',
  'var(--st-author-10)',
];

function authorColor(id: string | null | undefined): string {
  if (!id) return AUTHOR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return AUTHOR_COLORS[Math.abs(hash) % AUTHOR_COLORS.length];
}

function parentFallbackLabel(state: 'active' | 'deleted' | 'hidden'): string {
  if (state === 'deleted') return 'Original take was deleted';
  if (state === 'hidden') return 'Original take unavailable';
  return 'In reply to another take';
}

function mediaHint(type: Contribution['contributionType'] | null | undefined): string {
  if (type === 'photo') return '[Photo]';
  if (type === 'audio') return '[Audio]';
  if (type === 'video') return '[Video]';
  return '';
}

export function ContributionCard({
  contribution,
  authorName,
  onBuildOn,
  onJumpToParent,
  onToggleReplies,
  repliesExpanded = false,
  replyCount = 0,
  cardRef,
  compact = false,
  authorColor: compactBorderColor,
  threadId,
  reactions,
  onReact,
}: ContributionCardProps) {
  const displayName = authorName ?? contribution.author?.displayName;
  const canReact = !!onReact && !!threadId;
  const reactionCounts = reactions?.counts ?? {};
  const myReactions = reactions?.mine ?? [];
  const hasParent = !!contribution.buildsOnContributionId;
  const context = contribution.buildOnContext;
  const contextState = context?.state ?? (hasParent ? 'hidden' : null);
  const parentAuthor = context?.author?.displayName ?? 'another take';
  const parentPreview = context?.previewText ?? mediaHint(context?.contributionType);
  const parentLabel = contextState === 'active'
    ? `In reply to ${parentAuthor}`
    : contextState
      ? parentFallbackLabel(contextState)
      : null;
  const showReplyToggle = replyCount > 0 && !!onToggleReplies;
  const mediaDuration = contribution.mediaDuration ? formatTime(Math.round(contribution.mediaDuration)) : null;
  const waveform = normalizeWaveform(contribution.metadata?.waveform ?? []);

  return (
    <div
      ref={cardRef}
      class={`${styles.card} ${hasParent ? styles.replyCard : ''} ${compact ? styles.compactCard : ''}`}
      id={`contribution-${contribution.id}`}
      style={compact && compactBorderColor ? { borderLeftColor: compactBorderColor } : undefined}
    >
      {hasParent && <span aria-hidden="true" class={styles.connectorRail} />}

      {hasParent && parentLabel && (
        <button
          type="button"
          class={styles.parentContext}
          onClick={() => {
            if (contribution.buildsOnContributionId && onJumpToParent) {
              onJumpToParent(contribution.buildsOnContributionId);
            }
          }}
          disabled={!contribution.buildsOnContributionId || !onJumpToParent}
        >
          <span class={styles.parentArrow}>↪</span>
          <span class={styles.parentTextWrap}>
            <span class={styles.parentLabel}>{parentLabel}</span>
            {contextState === 'active' && parentPreview && (
              <span class={styles.parentPreview}>{parentPreview}</span>
            )}
          </span>
        </button>
      )}

      {!compact && (
        <div class={styles.authorRow}>
          {displayName && (
            <>
              <div
                class={styles.avatar}
                style={{ background: authorColor(contribution.authorId) }}
                title={displayName}
              >
                {getInitials(displayName)}
              </div>
              <div class={styles.authorMeta}>
                <span class={styles.authorName}>{displayName}</span>
                <span class={styles.time}>{relativeTime(contribution.createdAt)}</span>
              </div>
            </>
          )}
          {!displayName && (
            <span class={styles.time}>{relativeTime(contribution.createdAt)}</span>
          )}
          {contribution.visibility === 'private' && (
            <span class={styles.privateBadge} title="Private — only visible to the creator">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Private
            </span>
          )}
        </div>
      )}
      {compact && (
        <span class={styles.compactTime}>{relativeTime(contribution.createdAt)}</span>
      )}

      {contribution.contributionType === 'photo' && contribution.mediaUrl && isValidMediaUrl(contribution.mediaUrl) && (
        <img
          src={contribution.mediaUrl}
          alt="Photo contribution"
          loading="lazy"
          class={styles.photo}
        />
      )}

      {contribution.contributionType === 'audio' && contribution.mediaUrl && isValidMediaUrl(contribution.mediaUrl) && (
        <div class={styles.mediaBlock}>
          <div class={styles.mediaHeader}>
            <span class={styles.mediaBadge}>Voice note</span>
            {mediaDuration && <span class={styles.mediaDuration}>{mediaDuration}</span>}
          </div>
          {waveform.length > 0 && (
            <>
            <span class={sharedStyles.srOnly}>Voice note waveform</span>
            <div class={styles.waveform} aria-hidden="true">
              {waveform.map((sample, index) => (
                <span
                  key={`${sample}-${index}`}
                  class={styles.waveformBar}
                  style={{ height: `${Math.max(14, sample * 100)}%` }}
                />
              ))}
            </div>
            </>
          )}
          <audio
            class={styles.audioPlayer}
            controls
            preload="metadata"
            src={contribution.mediaUrl}
          />
          <p class={styles.transcript}>
            {typeof contribution.metadata?.transcription === 'string' && contribution.metadata.transcription.length > 0
              ? contribution.metadata.transcription
              : 'Captions generating…'}
          </p>
        </div>
      )}

      {contribution.contributionType === 'video' && contribution.mediaUrl && isValidMediaUrl(contribution.mediaUrl) && (
        <div class={styles.mediaBlock}>
          <div class={styles.mediaHeader}>
            <span class={styles.mediaBadge}>Video</span>
            {mediaDuration && <span class={styles.mediaDuration}>{mediaDuration}</span>}
          </div>
          <video
            class={styles.videoPlayer}
            controls
            playsInline
            preload="metadata"
            poster={contribution.mediaThumbnailUrl ?? undefined}
            src={contribution.mediaUrl}
          />
          <p class={styles.transcript}>
            {typeof contribution.metadata?.transcription === 'string' && contribution.metadata.transcription.length > 0
              ? contribution.metadata.transcription
              : 'Captions generating…'}
          </p>
        </div>
      )}

      {contribution.metadata?.promptText && (
        <p class={styles.promptContext}>
          <span class={styles.promptContextLabel}>Prompted:</span> {contribution.metadata.promptText}
        </p>
      )}

      {contribution.content && (
        <p class={styles.content}>
          {contribution.content}
        </p>
      )}

      {canReact && (
        <div class={styles.reactionBar} role="group" aria-label="React to this take">
          {REACTION_RAIL.map(({ type, emoji, label }) => {
            const count = reactionCounts[type] ?? 0;
            const mine = myReactions.includes(type);
            return (
              <button
                key={type}
                type="button"
                class={`${styles.reactionButton} ${mine ? styles.reactionButtonActive : ''}`}
                aria-label={mine ? `Remove ${label} reaction` : `React with ${label}`}
                aria-pressed={mine}
                onClick={() => onReact?.(contribution.id, type)}
              >
                <span class={styles.reactionEmoji} aria-hidden="true">{emoji}</span>
                {count > 0 && <span class={styles.reactionCount}>{count}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div class={styles.cardFooter}>
        {contribution.templatePhase && (
          <span class={styles.phaseBadge}>{contribution.templatePhase}</span>
        )}

        {showReplyToggle && (
          <button
            type="button"
            class={styles.replyToggle}
            onClick={() => onToggleReplies?.(contribution)}
          >
            {replyCount} riff{replyCount === 1 ? '' : 's'} {repliesExpanded ? '▴' : '▾'}
          </button>
        )}

        {onBuildOn && (
          <button
            type="button"
            class={styles.buildOnButton}
            onClick={() => onBuildOn(contribution)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 10 20 15 15 20" />
              <path d="M4 4v7a4 4 0 0 0 4 4h12" />
            </svg>
            Build On
          </button>
        )}
      </div>
    </div>
  );
}
